import { useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { Q_BILLING, M_TOPUP, M_CREATE_CHECKOUT } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

const TOPUP_PACKS = [500, 1000, 2500, 5000];
const PROVIDERS = [
  { id: 'razorpay', label: 'Razorpay' },
  { id: 'stripe', label: 'Stripe' },
];

export default function BillingRoute() {
  const { toast } = useUi();
  const [billing, refetch] = useQuery({ query: Q_BILLING });
  const [, topUp] = useMutation(M_TOPUP);
  const [, createCheckout] = useMutation(M_CREATE_CHECKOUT);

  const [provider, setProvider] = useState('razorpay');
  const [busy, setBusy] = useState(false);

  const data = billing.data?.billingUsage;
  const plans: any[] = data?.plans ?? [];
  const currentPlan = data?.plan;

  const doTopUp = async (amount: number) => {
    setBusy(true);
    const r = await topUp({ amount });
    setBusy(false);
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast(`+${amount.toLocaleString()}⚡ credits added`);
    refetch({ requestPolicy: 'network-only' });
  };

  const checkout = async (kind: string, planId?: string, creditAmount?: number) => {
    setBusy(true);
    const r = await createCheckout({
      input: { provider, kind, planId, creditAmount, successUrl: `${window.location.origin}/app/billing` },
    });
    setBusy(false);
    if (r.error) { toast(r.error.message, 'error'); return; }
    const s = r.data?.createCheckout;
    if (s?.redirectUrl) { window.location.href = s.redirectUrl; return; }
    if (s?.razorpayOrderId || s?.razorpaySubscriptionId) {
      toast(`Razorpay ${s.razorpaySubscriptionId ? 'subscription' : 'order'} created (${s.keyId})`);
    } else {
      toast('Checkout session created');
    }
  };

  if (billing.fetching) return <div className="p-8 text-stone-500">Loading billing…</div>;

  return (
    <div className="p-8 space-y-8 max-w-5xl">
      <h1 className="text-2xl font-semibold text-stone-900">Billing &amp; usage</h1>

      {/* Current state */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <p className="text-[11px] uppercase tracking-wide text-stone-400">Current plan</p>
          <p className="text-xl font-semibold text-stone-900 mt-1 capitalize">{currentPlan ?? '—'}</p>
          {data?.renewsAt && <p className="text-xs text-stone-500 mt-1">Renews {new Date(data.renewsAt).toLocaleDateString()}</p>}
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <p className="text-[11px] uppercase tracking-wide text-stone-400">Credits remaining</p>
          <p className="text-xl font-semibold text-stone-900 mt-1">{(data?.creditsRemaining ?? 0).toLocaleString()}⚡</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <p className="text-[11px] uppercase tracking-wide text-stone-400">AI billing</p>
          <p className="text-xl font-semibold text-stone-900 mt-1 capitalize">{data?.aiBillingMode ?? '—'}</p>
        </div>
      </div>

      {/* Provider toggle */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-stone-600">Pay with</span>
        <div className="flex gap-1 bg-stone-100 rounded-lg p-1">
          {PROVIDERS.map((p) => (
            <button key={p.id} onClick={() => setProvider(p.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ${provider === p.id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Plans */}
      <div>
        <h2 className="font-semibold text-stone-900 mb-3">Plans</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan;
            return (
              <div key={p.id} className={`rounded-xl p-5 border ${isCurrent ? 'border-violet-400 ring-1 ring-violet-200 bg-violet-50' : 'border-stone-200 bg-white'}`}>
                <p className="font-semibold text-stone-900">{p.name}</p>
                <p className="text-2xl font-bold text-stone-900 mt-2">${p.price}<span className="text-sm font-normal text-stone-400">/mo</span></p>
                <ul className="mt-3 space-y-1 text-xs text-stone-600">
                  <li>{p.credits?.toLocaleString()}⚡ credits</li>
                  <li>{p.seats} seats</li>
                  <li>{p.storage} storage</li>
                  {(p.features ?? []).slice(0, 4).map((f: string) => <li key={f}>✓ {f}</li>)}
                </ul>
                <button disabled={isCurrent || busy} onClick={() => checkout('plan_subscription', p.id)}
                  className={`mt-4 w-full py-2 rounded-lg text-sm font-medium ${isCurrent ? 'bg-stone-100 text-stone-400 cursor-default' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}>
                  {isCurrent ? 'Current plan' : 'Upgrade'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Credit top-ups */}
      <div>
        <h2 className="font-semibold text-stone-900 mb-1">Top up credits</h2>
        <p className="text-xs text-stone-500 mb-3">One-time credit packs — added instantly to your balance.</p>
        <div className="flex flex-wrap gap-3">
          {TOPUP_PACKS.map((amt) => (
            <button key={amt} disabled={busy} onClick={() => doTopUp(amt)}
              className="px-5 py-3 bg-white border border-stone-200 hover:border-violet-300 hover:bg-violet-50 rounded-xl text-sm font-medium text-stone-700 disabled:opacity-50">
              +{amt.toLocaleString()}⚡
              <span className="block text-[11px] text-stone-400 font-normal">${Math.max(5, Math.round(amt * 0.02))} via {provider}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
